/*
 * What the page shows while a research-paper submission is in flight.
 *
 * That request is SYNCHRONOUS and slow. Measured on staging for a 5.9 MB PDF:
 * 49s to read the paper, 25s to plan the beats, 28s to render the assets — 102
 * seconds during which the page previously showed nothing at all. `isSubmitting`
 * only changed the send button's accessible name, so the visible result of
 * pressing send was that the box stopped responding. Every user reads that as a
 * hang, and correctly.
 *
 * It reports ELAPSED time rather than progress, and says so. The request gives us
 * no phase signal — one POST, no stream, no polling — so a progress bar here
 * would be an animation pretending to be state. The elapsed count is real, and it
 * is the one thing that distinguishes "working" from "hung".
 *
 * The phase list is static and labelled as what happens, not as where we are, for
 * the same reason: stepping through it on a timer would look like status and be
 * theatre.
 *
 * What Cancel guarantees, stated narrowly because the obvious claim is too strong.
 * The request carries an AbortSignal all the way down to fetch, so pressing it closes
 * the connection and stops the upload rather than hiding the panel over an upload that
 * keeps running -- and nothing is charged, because the hold is placed after the job is
 * created, which is after this request returns.
 *
 * What it does NOT guarantee is that the server stops. The papers route creates the
 * job row before it reads the paper, so a Cancel late in the wait leaves work running
 * that the user can neither see nor confirm, with the client having dropped its id.
 * Closing that needs a cancel call to follow the abort, and there is no endpoint for
 * it yet; the honest version of the promise is the one above.
 */
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Spinner } from '@/shared/ui';

/* Measured median, not a guess: the run above took 102s. Rounded up, because a
   stated estimate that is usually beaten reads as honest and one that is usually
   missed reads as broken. */
const TYPICAL_SECONDS = 120;

type Props = {
  /** What is being read, for the heading. A filename, a title, or a URL. */
  subject: string;
  /**
   * Abandons the request. Omit it and no button is drawn.
   *
   * Deliberately optional, and deliberately not defaulted to a no-op: a Cancel that
   * only hides the panel while the upload keeps running is a control that lies about
   * what it does. If a caller cannot really stop the work, it should not offer to.
   */
  onCancel?: () => void;
  /**
   * Where focus goes when this panel leaves, if the control it took focus from
   * is gone by then. Supply the one element that outlives every path -- for the
   * prompt page that is the textarea the user types into next.
   */
  returnFocusTo?: RefObject<HTMLElement | null>;
};

export function PaperPending({ subject, onCancel, returnFocusTo }: Props) {
  const [elapsed, setElapsed] = useState(0);
  const panel = useRef<HTMLDivElement>(null);

  /* This panel REPLACES the candidate list, so the button the user just pressed
     is unmounted in the same render that mounts this. Focus then falls to
     <body>: keyboard focus is lost and a screen reader loses its place at the
     exact moment there is something worth saying. Moving focus here is what
     makes the replacement continuous rather than a hole -- and because the panel
     is the live region, landing on it is also how the one useful announcement
     gets made.

     Handing it back on unmount is the same argument at the other end of the
     wait, and taking without returning is the identical hole: the panel goes,
     nothing else has focus, and a keyboard user is at <body> tabbing from the
     top of the document. It bites hardest on the error path, where the panel is
     replaced by a banner after ~100 seconds and the box they need to correct is
     the whole length of the page away.

     Layout, not passive: the cleanup has to run BEFORE React detaches the node,
     or `document.activeElement` has already fallen to <body> and the panel can
     no longer tell whether it still held focus. */
  useLayoutEffect(() => {
    /* May already be <body> -- on the candidate path the row that was pressed
       unmounts in the same commit that mounts this panel, so there is nothing
       left to hand back to and `returnFocusTo` is the only answer. */
    const active = document.activeElement;
    const origin = active instanceof HTMLElement && active !== document.body ? active : null;
    /* Read both nodes here rather than in the cleanup. Neither is re-created
       while this panel is up -- the panel is one div and the fallback lives in
       the form, which outlives every paper path -- and reading a ref from a
       cleanup is the pattern that goes stale when that stops being true. */
    const node = panel.current;
    const fallback = returnFocusTo?.current;

    node?.focus();

    return () => {
      /* Only if the panel still holds it. Anything else focused by now -- the
         confirmation modal on the success path -- took it deliberately, and
         pulling focus back out of that would fight the thing the user is being
         shown.

         Deliberately untested, which is worth saying rather than covering with
         an assertion that cannot fail: ReactDOM records `document.activeElement`
         before it mutates the host tree and puts it back afterwards, so a
         cleanup that grabs focus while another element holds it is overridden
         anyway. Measured -- with this line deleted the cleanup does focus the
         fallback, and the pre-commit element still ends up with it. The line
         stays because it states the precondition in the one place that can see
         it, and does not depend on that restoration continuing to happen. */
      if (!node?.contains(document.activeElement)) return;
      /* The origin survives the paste-link and attach-file paths, where the
         send button lives in the form and the form never unmounts. */
      const target = origin?.isConnected ? origin : fallback;
      /* Connected, because focusing a detached node silently does nothing and
         leaves the hole this whole effect exists to close. */
      if (target?.isConnected) target.focus();
    };
    /* A ref object is a stable identity, so this does not re-run and re-steal
       focus mid-wait. */
  }, [returnFocusTo]);

  useEffect(() => {
    const started = Date.now();
    const tick = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => window.clearInterval(tick);
  }, []);

  /* `>=`, not `>`. At `>` the copy flips at 121s -- one second after the
     estimate it is referring to -- so for a whole second the panel claims the
     job is inside an estimate it has already passed. */
  const over = elapsed >= TYPICAL_SECONDS;

  return (
    <div
      className="paper-pending"
      role="status"
      aria-live="polite"
      ref={panel}
      /* Focusable by script only, never by tab: it is a destination for the
         focus the unmounted button dropped, not a control. */
      tabIndex={-1}
    >
      <div className="paper-pending__head">
        {/* No label: the panel itself is the live region, and a labelled
            Spinner nests a second role="status" inside it — which announces
            the same work twice. */}
        <Spinner size={18} />
        <h2 className="paper-pending__title">Reading {subject}</h2>
        {/* Hidden from the accessibility tree, deliberately. This span sits
            inside the panel's own aria-live region and changes every second, so
            announced it would re-read the whole region ~120 times per
            submission -- burying the one thing worth hearing under a stream of
            tick counts. It is visual reassurance; the words around it are the
            information. */}
        <span className="paper-pending__elapsed" aria-hidden="true">
          {elapsed}s
        </span>
      </div>

      <ol className="paper-pending__steps">
        <li>Reading the paper — equations, tables and figures</li>
        <li>Planning the beats and choosing how to draw each one</li>
        <li>Drawing them: typesetting the equations, redrawing the charts</li>
      </ol>

      <p className="paper-pending__note">
        {over
          ? 'Taking longer than usual. It is still working — a long paper with ' +
            'many tables takes more drawing. You will still get a cost estimate ' +
            'to confirm before anything is charged.'
          : `This usually takes about ${TYPICAL_SECONDS} seconds. You will get a ` +
            'cost estimate to confirm before anything is charged.'}
      </p>

      {onCancel && (
        <button type="button" className="paper-pending__cancel" onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );
}
